/**
 * AzureFXCreditPanel.jsx
 * ======================
 * Painel de gerenciamento de cotações USD/BRL e crédito Azure.
 *
 * Funcionalidades:
 *  - Gauge de crédito Azure ($250K total / consumido / saldo)
 *  - Tabela de cotações mensais com edição inline
 *  - Freeze de meses encerrados (cotação de fechamento)
 *  - Toggle de moeda (R$ / US$) sincronizado com localStorage
 *
 * Endpoints usados:
 *  GET  /planning/azure-fx/rates
 *  PUT  /planning/azure-fx/rates
 *  POST /planning/azure-fx/rates/{month}/freeze
 *  GET  /planning/azure-credits
 *  PUT  /planning/azure-credits
 *  GET  /planning/azure-fx/totals
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Cloud, Lock, Unlock, RefreshCw, DollarSign, TrendingDown, Save, Edit2 } from 'lucide-react';
import {
  getAzureFXRates, upsertAzureFXRate, freezeAzureFXMonth,
  getAzureCredits, updateAzureCredits, getAzureFXTotals,
} from '../../services/financialPlanningService';

const BRAND_GREEN = '#064e3b';
const BRAND_GOLD  = '#b48c2c';

const fmtUSD = (v) => `$${(v || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtBRL = (v) => `R$${(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`;

const MONTH_ABBR = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez',
};
const fmtMonth = (mk) => {
  if (!mk) return mk;
  const [y, m] = mk.split('-');
  return `${MONTH_ABBR[m] || m}/${y?.slice(2)}`;
};

// ─── Credit Gauge ───────────────────────────────────────────────────────────
const CreditGauge = ({ credit, onUpdate }) => {
  const [editing, setEditing]   = useState(false);
  const [totalVal, setTotalVal] = useState('');
  const [spentVal, setSpentVal] = useState('');
  const [saving, setSaving]     = useState(false);

  const pct     = Math.min(100, credit?.consumed_pct || 0);
  const spent   = credit?.spent_usd || 0;
  const total   = credit?.total_usd || 250000;
  const remain  = credit?.remaining_usd || (total - spent);
  const exhausted = !!credit?.is_exhausted;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAzureCredits({
        total_usd:          totalVal ? parseFloat(totalVal) : undefined,
        spent_usd_override: spentVal ? parseFloat(spentVal) : undefined,
      });
      setEditing(false);
      onUpdate && onUpdate();
    } catch (e) {
      console.error(e);
    }
    setSaving(false);
  };

  const barColor = exhausted ? '#ef4444' : pct > 70 ? '#f59e0b' : '#3b82f6';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Cloud size={18} className="text-blue-600" />
          <h3 className="font-bold text-gray-900">Crédito Azure</h3>
          {credit?._fallback && (
            <span className="text-[10px] bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
              migration 313 pendente
            </span>
          )}
        </div>
        <button
          onClick={() => { setEditing(!editing); setTotalVal(String(total)); setSpentVal(''); }}
          className="text-xs flex items-center gap-1 text-gray-500 hover:text-gray-700"
        >
          <Edit2 size={12} /> Editar
        </button>
      </div>

      {/* Gauge bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Consumido: <strong>{fmtUSD(spent)}</strong></span>
          <span>Saldo: <strong className={exhausted ? 'text-red-600' : 'text-blue-700'}>{fmtUSD(remain)}</strong></span>
        </div>
        <div className="h-4 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-4 rounded-full transition-all duration-500"
            style={{ width: `${pct}%`, backgroundColor: barColor }}
          />
        </div>
        <div className="flex justify-between text-[10px] text-gray-400 mt-1">
          <span>0</span>
          <span className={`font-bold ${exhausted ? 'text-red-500' : 'text-gray-600'}`}>
            {pct.toFixed(1)}% do total {fmtUSD(total)}
          </span>
          <span>{fmtUSD(total)}</span>
        </div>
        {exhausted && (
          <div className="mt-2 text-xs text-red-700 bg-red-50 rounded px-3 py-1.5">
            ⚠️ Crédito esgotado — custos Azure impactam o caixa
          </div>
        )}
        {!exhausted && (
          <div className="mt-2 text-[11px] text-blue-700 bg-blue-50 rounded px-3 py-1.5">
            ✅ Custos Azure são cobertos pelo crédito (não saem do caixa)
          </div>
        )}
      </div>

      {/* Edit form */}
      {editing && (
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-32">Total crédito (USD):</label>
            <input
              type="number" value={totalVal} onChange={e => setTotalVal(e.target.value)}
              className="flex-1 text-sm border rounded px-2 py-1"
              placeholder="250000"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-600 w-32">Gasto manual (USD):</label>
            <input
              type="number" value={spentVal} onChange={e => setSpentVal(e.target.value)}
              className="flex-1 text-sm border rounded px-2 py-1"
              placeholder="(auto da tabela azure)"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="text-xs px-3 py-1 border rounded">Cancelar</button>
            <button
              onClick={handleSave} disabled={saving}
              className="text-xs px-3 py-1 rounded text-white font-bold"
              style={{ backgroundColor: BRAND_GREEN }}
            >
              {saving ? '…' : <><Save size={10} className="inline mr-1" />Salvar</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── FX Rate Row ─────────────────────────────────────────────────────────────
const FXRateRow = ({ row, totals, onSaved, onFreeze }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal]         = useState(String(row.usd_brl || 5.70));
  const [saving, setSaving]   = useState(false);

  const frozen  = !!row.is_frozen;
  const monthly = totals?.find(t => t.month_key === row.month_key);
  const costUSD = monthly?.total_usd || 0;
  const usdBrl  = row.usd_brl || 5.70;
  const costBRL = costUSD * usdBrl;

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertAzureFXRate({ month_key: row.month_key, usd_brl: parseFloat(val) });
      setEditing(false);
      onSaved && onSaved();
    } catch (e) {
      alert(e?.response?.data?.error || 'Erro ao salvar cotação.');
    }
    setSaving(false);
  };

  const handleFreeze = async () => {
    await onFreeze(row.month_key, frozen);
  };

  return (
    <tr className={`border-b hover:bg-gray-50 text-sm ${frozen ? 'opacity-80' : ''}`}>
      <td className="px-3 py-2 font-medium text-gray-800">
        <div className="flex items-center gap-1.5">
          {frozen ? <Lock size={12} className="text-gray-400" /> : <Unlock size={12} className="text-emerald-500" />}
          {fmtMonth(row.month_key)}
        </div>
      </td>
      <td className="px-3 py-2 text-right">
        {editing && !frozen ? (
          <div className="flex items-center justify-end gap-1">
            <span className="text-xs text-gray-400">R$</span>
            <input
              type="number" step="0.01" value={val}
              onChange={e => setVal(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              className="w-20 text-xs border rounded px-2 py-0.5 text-right"
              autoFocus
            />
            <button onClick={handleSave} disabled={saving}
              className="text-[11px] px-2 py-0.5 rounded text-white" style={{ backgroundColor: BRAND_GREEN }}>
              {saving ? '…' : '✓'}
            </button>
            <button onClick={() => { setEditing(false); setVal(String(row.usd_brl)); }}
              className="text-[11px] px-1.5 py-0.5 rounded border">✕</button>
          </div>
        ) : (
          <button
            onClick={() => !frozen && setEditing(true)}
            className={`font-mono text-sm ${frozen ? 'cursor-default text-gray-500' : 'cursor-pointer text-blue-700 hover:underline'}`}
            title={frozen ? 'Mês congelado' : 'Clique para editar'}
          >
            {Number(usdBrl).toFixed(4)}
          </button>
        )}
      </td>
      <td className="px-3 py-2 text-right font-mono text-gray-600 text-xs">{fmtUSD(costUSD)}</td>
      <td className="px-3 py-2 text-right font-mono text-gray-600 text-xs">{fmtBRL(costBRL)}</td>
      <td className="px-3 py-2 text-center">
        {frozen ? (
          <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
            🔒 {row.frozen_at ? new Date(row.frozen_at).toLocaleDateString('pt-BR') : 'congelado'}
          </span>
        ) : (
          <button
            onClick={handleFreeze}
            className="text-[11px] flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded hover:bg-amber-200 mx-auto"
          >
            <Lock size={10} /> Congelar
          </button>
        )}
      </td>
    </tr>
  );
};

// ─── Main Panel ──────────────────────────────────────────────────────────────
const AzureFXCreditPanel = () => {
  const [rates, setRates]     = useState([]);
  const [totals, setTotals]   = useState([]);
  const [credit, setCredit]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState(
    () => localStorage.getItem('finops_currency') || 'BRL'
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ratesRes, totalsRes, creditRes] = await Promise.all([
        getAzureFXRates(24),
        getAzureFXTotals(13),
        getAzureCredits(),
      ]);
      setRates(ratesRes?.data || []);
      setTotals(totalsRes?.data || []);
      setCredit(creditRes?.data || null);
    } catch (e) {
      console.error('[AzureFXCreditPanel] load error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFreeze = async (monthKey, currentlyFrozen) => {
    try {
      await freezeAzureFXMonth(monthKey, currentlyFrozen /* undo if already frozen */);
      load();
    } catch (e) {
      console.error(e);
    }
  };

  const handleCurrencyToggle = (c) => {
    setCurrency(c);
    localStorage.setItem('finops_currency', c);
    // Dispatch custom event so DRE/Cashflow tabs sync if open
    window.dispatchEvent(new StorageEvent('storage', { key: 'finops_currency', newValue: c }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-40 bg-white rounded-xl animate-pulse border" />
        <div className="h-64 bg-white rounded-xl animate-pulse border" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Credit Gauge */}
      <CreditGauge credit={credit} onUpdate={load} />

      {/* FX Rates Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-2">
            <DollarSign size={16} style={{ color: BRAND_GOLD }} />
            <h3 className="font-bold text-gray-900">Cotações USD/BRL por Mês</h3>
          </div>
          <div className="flex items-center gap-3">
            {/* Currency toggle */}
            <div className="flex items-center border rounded overflow-hidden text-xs">
              <button
                onClick={() => handleCurrencyToggle('BRL')}
                className={`px-2 py-1 font-bold transition-colors ${currency === 'BRL' ? 'bg-emerald-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >R$</button>
              <button
                onClick={() => handleCurrencyToggle('USD')}
                className={`px-2 py-1 font-bold transition-colors ${currency === 'USD' ? 'bg-blue-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >US$</button>
            </div>
            <button onClick={load} className="text-xs text-gray-400 hover:text-gray-600">
              <RefreshCw size={13} />
            </button>
          </div>
        </div>
        <div className="text-[10px] text-gray-400 px-5 py-2 bg-gray-50 border-b">
          <Lock size={9} className="inline mr-1" /> Meses congelados usam a cotação de fechamento.
          Meses abertos podem ser editados — clique no valor para alterar.
          Congele ao encerrar o mês.
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-xs text-gray-500 border-b">
                <th className="px-3 py-2 text-left">Mês</th>
                <th className="px-3 py-2 text-right">Cotação USD/BRL</th>
                <th className="px-3 py-2 text-right">Custo Azure (USD)</th>
                <th className="px-3 py-2 text-right">Custo Azure (BRL)</th>
                <th className="px-3 py-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-5 py-8 text-center text-sm text-gray-400">
                    Nenhuma cotação encontrada. Execute a migration 313 para popular as cotações iniciais.
                  </td>
                </tr>
              ) : (
                rates.map(r => (
                  <FXRateRow
                    key={r.month_key}
                    row={r}
                    totals={totals}
                    onSaved={load}
                    onFreeze={handleFreeze}
                  />
                ))
              )}
            </tbody>
            {rates.length > 0 && (() => {
              const grandUSD = totals.reduce((s, t) => s + (t.total_usd || 0), 0);
              const grandBRL = totals.reduce((s, t) => s + (t.total_brl || 0), 0);
              return (
                <tfoot>
                  <tr className="bg-gray-100 border-t font-bold text-sm">
                    <td className="px-3 py-2 text-gray-700" colSpan={2}>Total acumulado</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtUSD(grandUSD)}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtBRL(grandBRL)}</td>
                    <td />
                  </tr>
                </tfoot>
              );
            })()}
          </table>
        </div>
      </div>

      <div className="text-[10px] text-gray-400 text-right">
        Fonte: azure_usage_monthly_summary (migration 311) × azure_monthly_fx_rates (migration 313)
      </div>
    </div>
  );
};

export default AzureFXCreditPanel;
